<template>
  <gp-map-popup :caption="path.displayName"
                :surface="surface">
    <template v-if="!readonly" #help>
      <rui-help-button-content :caption="$t('js.garden.paths.popup.help.caption')" :lines="helpLines" />
    </template>

    <template v-if="!readonly">
      <!--
        Handler should display the observation form for this path
        @event new-observation
      -->
      <rui-button icon="observations" :title="$t('generic.new_observation')" @click="$emitBus('show-observation-form', path)" />
      <!--
        Handler should display the task form for this path
        @event new-task
      -->
      <rui-button icon="calendar-plus" :title="$t('generic.new_task')" @click="$emitBus('show-task-form', path)" />
    </template>

    <!--
      Handler should display the observations for this path
      @event show-observations
    -->
    <rui-button v-if="readonly && path.observationsCount > 0"
                icon="notebook"
                :title="$t('js.generic.observations')"
                @click="$emitBus('show-observations', path)">
      <template v-if="readonly">
        {{ $t('js.generic.observations') }}
        <small>({{ path.observationsCount }})</small>
      </template>
    </rui-button>
    <rui-button v-else-if="!readonly"
                icon="notebook"
                :title="$t('js.generic.observations')"
                :disabled="path.observationsCount === 0"
                @click="$emitBus('show-observations', path)" />

    <template v-if="!readonly">
      <rui-toolbar-separator />

      <!--
        Handler should enter in "geometry transformation" mode for this path
        @event transform
      -->
      <rui-button :icon="shapeIcons[path.geometryType].edit"
                  :title="$t('js.generic.transform')"
                  @click="$emitBus('transform-path', path)" />

      <!--
        Handler should enter in "geometry rotation" mode for this path
        @event rotate
      -->
      <rui-button v-if="['Polygon', 'LineString'].indexOf(path.geometryType) > -1"
                  icon="arrow-clockwise"
                  :title="$t('js.generic.rotate')"
                  @click="$emitBus('rotate-path', path)" />

      <!--
        Handler should enter in "geometry move" mode for this path
        @event move
      -->
      <rui-button icon="arrows-out-cardinal" :title="$t('js.generic.move')" @click="$emitBus('move-path', path)" />

      <rui-toolbar-separator />

      <!--
        Handler should display the form for this path
        @event edit
      -->
      <rui-button icon="pencil" :title="$t('generic.edit')" @click="$emitBus('show-path-form', path)" />

      <rui-toolbar-separator />

      <!--
        Handler should destroy this path
        @event edit
      -->
      <rui-button icon="trash"
                  variant="destructive"
                  :title="$t('generic.destroy')"
                  @click="destroy" />
    </template>
  </gp-map-popup>
</template>

<script>
import RuiButton from '@experiments-labs/rise_ui/src/components/Button/Button.vue'
import RuiHelpButtonContent from '@experiments-labs/rise_ui/src/components/HelpButton/HelpButtonContent.vue'
import RuiToolbarSeparator from '@experiments-labs/rise_ui/src/components/Toolbar/ToolbarSeparator.vue'

import { markRaw } from 'vue'
import { formatGeometryMeasure } from '../../../../tools/MeasureFormatters'
import { shapeIcons } from '../../../../shapeIcons'
import Toaster from '../../../../tools/Toaster'
import Path from '../../../../classes/models/Path'
import GpMapPopup from '../../../../common/MapPopup/MapPopup.vue'
import GpaPathDestroyWarning from './_DestroyWarning.vue'

const rawDestroyWarning = markRaw(GpaPathDestroyWarning)

export default {
  name: 'GpaPathPopup',
  components: { RuiHelpButtonContent, RuiToolbarSeparator, RuiButton, GpMapPopup },
  props: {
    /** Path instance */
    path: { required: true, type: Path },
    /** When true, hide all the modification controls */
    readonly: { required: false, type: Boolean, default: false },
  },
  data () {
    return {
      shapeIcons,
      helpLines: [
        { icon: 'observations', text: this.$t('js.garden.paths.popup.help.new_observation') },
        { icon: 'calendar-plus', text: this.$t('js.garden.paths.popup.help.add_task') },
        { icon: 'notebook', text: this.$t('js.garden.paths.popup.help.display_observations') },
        { icon: shapeIcons[this.$props.path.geometryType].edit, text: this.$t('js.garden.paths.popup.help.edit_geometry') },
        { icon: 'arrow-clockwise', text: this.$t('js.garden.paths.popup.help.rotate') },
        { icon: 'arrows-out-cardinal', text: this.$t('js.garden.paths.popup.help.move') },
        { icon: 'pencil', text: this.$t('js.garden.paths.popup.help.edit') },
        { icon: 'trash', text: this.$t('js.garden.paths.popup.help.destroy'), variant: 'danger' },
      ],
    }
  },
  computed: {
    surface () { return formatGeometryMeasure(this.path) },
  },
  methods: {
    destroy () {
      this.$emitBus('ask-confirmation', {
        caption: this.$t('js.path.destroy_warning_title'),
        component: rawDestroyWarning,
        callback: () => {
          this.$store.dispatch('destroyPath', this.path.id)
            .then(() => { Toaster.success(this.$t('js.path.successfully_destroyed')) })
            .catch(() => { Toaster.error(this.$t('js.path.failed_to_destroy')) })
        },
      })
    },
  },
}
</script>
